import React, { useMemo, useState } from 'react';
import {
  Check, X, ChevronDown, Search, Share2, Settings2, Gauge,
  AlertTriangle, Globe, Smartphone, Monitor,
} from 'lucide-react';
import { analyseSeo, SCHEMA_TYPES } from '../utils/seoAnalysis';

/**
 * The SEO panel — snippet preview, focus keyword, live score, and the fields a writer
 * previously had to ask a developer for.
 *
 * Three tabs, matching the mental model editors already have from Rank Math and Yoast:
 * General is what appears in a search result, Social is what appears when the link is
 * pasted somewhere, Advanced is indexing and schema.
 *
 * The score is advisory and says so. The reason it is here is not the number — it is that
 * every failing check names the specific thing to fix, which is the part that is invisible
 * while you are writing and obvious three months later when the page has no traffic.
 *
 * Controlled component: it owns no state except which tab is open. The parent holds the
 * form, so saving, dirty-tracking and validation stay in one place.
 */

const SITE = 'https://rostery.com.au';

function ScoreRing({ score, grade }) {
  const colour = grade === 'good' ? '#16a34a' : grade === 'ok' ? '#f59e0b' : '#dc2626';
  const r = 26;
  const circ = 2 * Math.PI * r;
  return (
    <div className="relative w-[68px] h-[68px] shrink-0">
      <svg width="68" height="68" viewBox="0 0 68 68" className="-rotate-90">
        <circle cx="34" cy="34" r={r} fill="none" stroke="#e2e8f0" strokeWidth="7" />
        <circle
          cx="34" cy="34" r={r} fill="none" stroke={colour} strokeWidth="7"
          strokeLinecap="round"
          strokeDasharray={circ}
          strokeDashoffset={circ - (circ * score) / 100}
          style={{ transition: 'stroke-dashoffset .45s ease, stroke .45s ease' }}
        />
      </svg>
      <div className="absolute inset-0 grid place-items-center">
        <span className="text-lg font-black tabular-nums" style={{ color: colour }}>{score}</span>
      </div>
    </div>
  );
}

function CharBar({ value, min, max, label }) {
  const len = String(value || '').length;
  const over = len > max;
  const under = len > 0 && len < min;
  const pct = Math.min(100, (len / max) * 100);
  return (
    <div className="mt-1.5">
      <div className="h-1 rounded-full bg-slate-100 overflow-hidden">
        <div
          className="h-full rounded-full transition-all"
          style={{
            width: `${pct}%`,
            background: over ? '#dc2626' : under ? '#f59e0b' : '#16a34a',
          }}
        />
      </div>
      <p className={`text-[11px] mt-1 ${over ? 'text-red-600' : under ? 'text-amber-600' : 'text-slate-400'}`}>
        {len}/{max} characters{over ? ' — will be truncated' : under ? ` — under ${min} wastes the snippet` : ''}
        {label ? ` · ${label}` : ''}
      </p>
    </div>
  );
}

function TestRow({ t }) {
  const [open, setOpen] = useState(false);
  return (
    <li className="border-b border-slate-100 last:border-0">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-start gap-2.5 py-2 text-left hover:bg-slate-50/70 px-1 rounded"
      >
        <span
          className={`mt-[3px] w-[15px] h-[15px] rounded-full grid place-items-center shrink-0 ${
            t.ok ? (t.partial ? 'bg-amber-500' : 'bg-emerald-500') : 'bg-red-500'
          }`}
        >
          {t.ok ? <Check size={10} className="text-white" strokeWidth={3.5} />
                : <X size={10} className="text-white" strokeWidth={3.5} />}
        </span>
        <span className="flex-1 min-w-0">
          <span className="text-[13px] text-slate-700 leading-snug">{t.label}</span>
          {t.value && <span className="ml-1.5 text-[11px] text-slate-400">({t.value})</span>}
        </span>
        <ChevronDown
          size={13}
          className={`mt-1 text-slate-300 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && t.hint && (
        <p className="text-[12px] text-slate-500 leading-relaxed pl-[26px] pr-2 pb-2.5">{t.hint}</p>
      )}
    </li>
  );
}

const field =
  'w-full px-3.5 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500/30';

export default function SeoPanel({
  value = {},
  onChange,
  kind = 'blog',          // blog | feature | page
  urlPrefix = '/blog/',
  title = '',
  content = '',
  excerpt = '',
  featuredImage = '',
  // marketing_features stores its meta on seo_title/seo_description while the other two
  // tables use meta_title/meta_description. Renaming a live column the marketing site reads
  // has no upside and a real chance of a blank <title> in production, so the panel adapts
  // to the column name instead.
  titleField = 'meta_title',
  descField = 'meta_description',
}) {
  const [tab, setTab] = useState('general');
  const [device, setDevice] = useState('desktop');

  const set = (k) => (e) => {
    const v = e && e.target
      ? (e.target.type === 'checkbox' ? e.target.checked : e.target.value)
      : e;
    onChange({ ...value, [k]: v });
  };

  const metaTitleValue = value[titleField] || '';
  const metaDescValue  = value[descField] || '';

  const secondaryList = useMemo(
    () => String(value.secondary_keywords || '')
      .split(',').map((s) => s.trim()).filter(Boolean),
    [value.secondary_keywords],
  );

  const result = useMemo(() => analyseSeo({
    title,
    metaTitle: metaTitleValue,
    metaDescription: metaDescValue,
    slug: value.slug,
    content,
    excerpt,
    focusKeyword: value.focus_keyword,
    secondaryKeywords: secondaryList,
    featuredImage,
    siteHost: 'rostery.com.au',
  }), [title, content, excerpt, featuredImage, secondaryList,
       metaTitleValue, metaDescValue, value.slug, value.focus_keyword]);

  const previewTitle = (metaTitleValue || title || 'Untitled') + ' | Rostery';
  const previewDesc  = metaDescValue
    || excerpt
    || 'No meta description set — Google will write its own from the page content.';
  const previewUrl   = `rostery.com.au${urlPrefix}${value.slug || ''}`;

  const schemaOptions = SCHEMA_TYPES[kind] || SCHEMA_TYPES.page;

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
      {/* Header: score + focus keyword, always visible regardless of tab */}
      <div className="p-6 pb-5 border-b border-slate-100">
        <div className="flex items-start gap-4">
          <ScoreRing score={result.score} grade={result.grade} />
          <div className="flex-1 min-w-0">
            <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wider flex items-center gap-2">
              <Gauge size={14} /> SEO analysis
            </h2>
            <p className="text-[12px] text-slate-500 mt-1 leading-relaxed">
              {result.passed} of {result.total} checks passing · {result.stats.words} words ·{' '}
              {result.stats.readingMinutes} min read
              {value.focus_keyword ? ` · ${result.stats.density}% density` : ''}
            </p>
            {!value.focus_keyword && (
              <p className="text-[12px] text-amber-600 mt-1.5 flex items-center gap-1.5">
                <AlertTriangle size={12} /> Set a focus keyword to check the rest.
              </p>
            )}
          </div>
        </div>

        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <div>
            <label className="block text-[13px] font-medium text-slate-700 mb-1.5">Focus keyword</label>
            <input
              type="text" value={value.focus_keyword || ''} onChange={set('focus_keyword')}
              className={field} placeholder="e.g. NDIS rostering software"
            />
          </div>
          <div>
            <label className="block text-[13px] font-medium text-slate-700 mb-1.5">
              Secondary keywords
              <span className="font-normal text-slate-400"> · comma separated</span>
            </label>
            <input
              type="text" value={value.secondary_keywords || ''} onChange={set('secondary_keywords')}
              className={field} placeholder="SCHADS payroll, shift scheduling"
            />
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-slate-100 px-3">
        {[
          ['general', 'Search', Search],
          ['social', 'Social', Share2],
          ['advanced', 'Advanced', Settings2],
        ].map(([id, label, Icon]) => (
          <button
            key={id} type="button" onClick={() => setTab(id)}
            className={`flex items-center gap-1.5 px-3.5 py-2.5 text-[13px] font-semibold border-b-2 -mb-px transition-colors ${
              tab === id
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-slate-400 hover:text-slate-600'
            }`}
          >
            <Icon size={14} /> {label}
          </button>
        ))}
      </div>

      <div className="p-6 space-y-5">
        {tab === 'general' && (
          <>
            {/* Snippet preview — the whole point of the panel: see the result, not the field. */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-[13px] font-medium text-slate-700">Search preview</span>
                <div className="flex gap-1">
                  {[['desktop', Monitor], ['mobile', Smartphone]].map(([d, Icon]) => (
                    <button
                      key={d} type="button" onClick={() => setDevice(d)}
                      className={`p-1.5 rounded-lg ${device === d ? 'bg-slate-900 text-white' : 'text-slate-400 hover:bg-slate-100'}`}
                      aria-label={d}
                    >
                      <Icon size={13} />
                    </button>
                  ))}
                </div>
              </div>
              <div className={`rounded-xl border border-slate-200 bg-white p-4 ${device === 'mobile' ? 'max-w-[380px]' : ''}`}>
                <div className="flex items-center gap-1.5 mb-1">
                  <span className="w-4 h-4 rounded-full bg-slate-900 grid place-items-center shrink-0">
                    <Globe size={9} className="text-white" />
                  </span>
                  <span className="text-[12px] text-slate-600 truncate">{previewUrl}</span>
                </div>
                <p className="text-[#1a0dab] text-[17px] leading-snug hover:underline cursor-pointer line-clamp-2">
                  {previewTitle}
                </p>
                <p className="text-[13px] text-slate-600 leading-[1.5] mt-0.5 line-clamp-2">{previewDesc}</p>
              </div>
            </div>

            <div>
              <label className="block text-[13px] font-medium text-slate-700 mb-1.5">SEO title</label>
              <input
                type="text" value={metaTitleValue} onChange={set(titleField)}
                className={field} placeholder={title || 'Defaults to the title above'}
              />
              <CharBar value={metaTitleValue || title} min={25} max={60} />
            </div>

            <div>
              <label className="block text-[13px] font-medium text-slate-700 mb-1.5">Meta description</label>
              <textarea
                value={metaDescValue} onChange={set(descField)} rows={3}
                className={`${field} resize-none`}
                placeholder="What this page answers, in one or two sentences."
              />
              <CharBar value={metaDescValue} min={120} max={158} />
            </div>

            {/* The checks */}
            <div className="pt-1">
              {result.groups.map((g) => {
                const failed = g.tests.filter((t) => !t.ok).length;
                return (
                  <details key={g.id} open={failed > 0} className="mb-2">
                    <summary className="flex items-center justify-between cursor-pointer list-none py-2 text-[13px] font-semibold text-slate-700">
                      <span>{g.label}</span>
                      <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${
                        failed === 0 ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-600'
                      }`}>
                        {g.tests.length - failed}/{g.tests.length}
                      </span>
                    </summary>
                    <ul className="mt-1">
                      {g.tests.map((t) => <TestRow key={t.id} t={t} />)}
                    </ul>
                  </details>
                );
              })}
            </div>
          </>
        )}

        {tab === 'social' && (
          <>
            <p className="text-[12px] text-slate-500 leading-relaxed">
              How the link looks when someone pastes it into LinkedIn, Slack or a message.
              Leave blank to reuse the SEO title and description above.
            </p>

            <div className="rounded-xl border border-slate-200 overflow-hidden">
              <div className="aspect-[1200/630] bg-slate-100 grid place-items-center overflow-hidden">
                {(value.og_image || featuredImage) ? (
                  <img
                    src={value.og_image || featuredImage} alt=""
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className="text-[12px] text-slate-400">
                    Falls back to the site card at /og-image.png
                  </span>
                )}
              </div>
              <div className="p-3 bg-slate-50">
                <p className="text-[10px] uppercase tracking-wide text-slate-400">rostery.com.au</p>
                <p className="text-[14px] font-semibold text-slate-900 line-clamp-1">
                  {value.og_title || metaTitleValue || title || 'Untitled'}
                </p>
                <p className="text-[12px] text-slate-500 line-clamp-2">
                  {value.og_description || metaDescValue || excerpt || '—'}
                </p>
              </div>
            </div>

            {[
              ['og_title', 'Social title', 'text'],
              ['og_description', 'Social description', 'textarea'],
              ['og_image', 'Social image URL', 'text'],
            ].map(([k, label, type]) => (
              <div key={k}>
                <label className="block text-[13px] font-medium text-slate-700 mb-1.5">{label}</label>
                {type === 'textarea' ? (
                  <textarea value={value[k] || ''} onChange={set(k)} rows={2}
                    className={`${field} resize-none`} placeholder="Reuses the search description" />
                ) : (
                  <input type="text" value={value[k] || ''} onChange={set(k)} className={field}
                    placeholder={k === 'og_image' ? 'https://… (1200×630)' : 'Reuses the SEO title'} />
                )}
              </div>
            ))}

            <details className="pt-1">
              <summary className="cursor-pointer text-[13px] font-semibold text-slate-700 py-1">
                Separate X / Twitter card
              </summary>
              <div className="space-y-3 mt-3">
                {[
                  ['twitter_title', 'X title', 'text'],
                  ['twitter_description', 'X description', 'textarea'],
                  ['twitter_image', 'X image URL', 'text'],
                ].map(([k, label, type]) => (
                  <div key={k}>
                    <label className="block text-[13px] font-medium text-slate-700 mb-1.5">{label}</label>
                    {type === 'textarea' ? (
                      <textarea value={value[k] || ''} onChange={set(k)} rows={2}
                        className={`${field} resize-none`} placeholder="Reuses the social description" />
                    ) : (
                      <input type="text" value={value[k] || ''} onChange={set(k)} className={field}
                        placeholder="Reuses the social values" />
                    )}
                  </div>
                ))}
              </div>
            </details>
          </>
        )}

        {tab === 'advanced' && (
          <>
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="flex items-start gap-2.5 rounded-xl border border-slate-200 p-3 cursor-pointer hover:border-slate-300">
                <input
                  type="checkbox"
                  checked={value.robots_index !== false}
                  onChange={set('robots_index')}
                  className="mt-0.5"
                />
                <span>
                  <span className="block text-[13px] font-medium text-slate-800">Allow indexing</span>
                  <span className="block text-[11px] text-slate-500 leading-snug mt-0.5">
                    Off adds noindex — the page stays reachable but drops out of search.
                  </span>
                </span>
              </label>

              <label className="flex items-start gap-2.5 rounded-xl border border-slate-200 p-3 cursor-pointer hover:border-slate-300">
                <input
                  type="checkbox"
                  checked={value.robots_follow !== false}
                  onChange={set('robots_follow')}
                  className="mt-0.5"
                />
                <span>
                  <span className="block text-[13px] font-medium text-slate-800">Follow links</span>
                  <span className="block text-[11px] text-slate-500 leading-snug mt-0.5">
                    Off adds nofollow to every link on the page.
                  </span>
                </span>
              </label>
            </div>

            {value.robots_index === false && (
              <p className="text-[12px] text-amber-700 bg-amber-50 border border-amber-200 rounded-xl p-3 flex gap-2">
                <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                This page will be removed from Google. It also stays out of the sitemap.
              </p>
            )}

            <div>
              <label className="block text-[13px] font-medium text-slate-700 mb-1.5">Canonical URL</label>
              <input
                type="text" value={value.canonical_url || ''} onChange={set('canonical_url')}
                className={field} placeholder={`${SITE}${urlPrefix}${value.slug || ''}`}
              />
              <p className="text-[11px] text-slate-400 mt-1">
                Only set this when the same content lives somewhere else and that copy should win.
              </p>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label className="block text-[13px] font-medium text-slate-700 mb-1.5">Schema type</label>
                <select value={value.schema_type || schemaOptions[0]} onChange={set('schema_type')} className={field}>
                  {schemaOptions.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <p className="text-[11px] text-slate-400 mt-1">
                  Pick HowTo or FAQPage only if the page really is one — markup must match what a reader sees.
                </p>
              </div>
              <div>
                <label className="block text-[13px] font-medium text-slate-700 mb-1.5">Breadcrumb title</label>
                <input
                  type="text" value={value.breadcrumb_title || ''} onChange={set('breadcrumb_title')}
                  className={field} placeholder="Shorter label for the trail"
                />
              </div>
            </div>

            <label className="flex items-start gap-2.5 rounded-xl border border-slate-200 p-3 cursor-pointer hover:border-slate-300">
              <input type="checkbox" checked={!!value.is_pillar} onChange={set('is_pillar')} className="mt-0.5" />
              <span>
                <span className="block text-[13px] font-medium text-slate-800">Pillar content</span>
                <span className="block text-[11px] text-slate-500 leading-snug mt-0.5">
                  Marks this as a cornerstone page — the one other posts on the topic should link to.
                </span>
              </span>
            </label>
          </>
        )}
      </div>
    </div>
  );
}
